import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  useTheme,
  alpha,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Skeleton,
  TextField,
  Button,
  CircularProgress,
  Stack,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  ChildCare as ChildCareIcon,
  Receipt as ReceiptIcon,
  Description as DescriptionIcon,
  ChevronRight as ChevronRightIcon,
  EventAvailable as EventAvailableIcon,
  Event as EventIcon,
  Paid as PaidIcon,
  WorkspacePremium as WorkspacePremiumIcon,
  Diamond as DiamondIcon,
  Star as StarIcon,
  CardMembership as CardMembershipIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Public as PublicIcon,
  DeleteOutline as DeleteOutlineIcon,
  PersonSearch as PersonSearchIcon,
  Login as LoginIcon,
  Logout as LogoutIcon,
  ConnectingAirports as ConnectingAirportsIcon,
  MeetingRoom as MeetingRoomIcon,
  Monitor as MonitorIcon,
  DirectionsWalk as DirectionsWalkIcon,
  Groups as GroupsIcon,
  BusinessCenter as BusinessCenterIcon,
  SupportAgent as SupportAgentIcon,
  Api as ApiIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';
import DashboardHeader from '../components/common/DashboardHeader';
import GuestDocumentSideEditor from '../components/customer/GuestDocumentSideEditor';
import ForeignGuestDetailsDrawer from '../components/booking/ForeignGuestDetailsDrawer';
import CustomerContactInfoDrawer from '../components/customer/CustomerContactInfoDrawer';
import CustomerCompanyDetailsDrawer, {
  clearCompanyDetailsApiPayload,
} from '../components/customer/CustomerCompanyDetailsDrawer';
import AlertDialog from '../components/common/AlertDialog';
import ChannelImage from '../components/common/ChannelImage';
import useStayGuestDrawer from '../hooks/useStayGuestDrawer';
import {
  getCustomerDetail,
  updateCustomer,
  updateBookingStayGuest,
} from '../services/api';
import { formatCountryLabel } from '../utils/countryDisplay';
import {
  formatAadhaarDisplay,
  isAadhaarDocumentType,
  isValidPassportIdNumber,
  normalizeDocumentIdForSubmit,
  normalizeDocumentIdInput,
  validateAadhaarIdNumber,
} from '../utils/documentIdFormatters';
import {
  clearForeignGuestApiPayload,
  foreignDetailsToGuestApiPayload,
  getForeignGuestDrawerCopy,
  hasForeignGuestDetailsValues,
  getForeignGuestChipSx,
  isForeignGuestCountry,
  mapGuestApiToForeignDetails,
} from '../utils/foreignGuestFormUtils';
import moment from 'moment';
import { formatHotelDate } from '../utils/dateUtils';
import { THEME_COLORS } from '../shared/constants/theme';
import {
  DASHBOARD_ACCENT,
  getDashboardDarkSurfaceTokens,
  getDashboardPageShellSx,
} from '../utils/dashboardSurfaceSx';
import { clearCachedBookingGuestDocuments } from '../utils/bookingGuestDocumentsCache';

const BOOKING_SOURCE_LABELS = {
  direct: 'Direct',
  booking_engine: 'Booking Engine',
  ota: 'OTA',
  walk_in: 'Walk-In',
  corporate: 'Corporate',
  group: 'Group',
  agent: 'Travel Agent',
  api: 'API',
  other: 'Other',
};

const normalizeBookingSourceKey = (raw) =>
  String(raw || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');

const formatBookingSourceLabel = (source) => {
  const key = normalizeBookingSourceKey(source);
  if (!key) return '—';
  if (BOOKING_SOURCE_LABELS[key]) return BOOKING_SOURCE_LABELS[key];
  return key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
};

const bookingSourceIconAndColor = (sourceRaw) => {
  const src = normalizeBookingSourceKey(sourceRaw);
  if (src === 'ota') return { Icon: ConnectingAirportsIcon, color: '#2563eb' };
  if (src === 'booking_engine') return { Icon: MeetingRoomIcon, color: '#14b8a6' };
  if (src === 'direct') return { Icon: MonitorIcon, color: '#6366f1' };
  if (src === 'walk_in') return { Icon: DirectionsWalkIcon, color: '#8b5cf6' };
  if (src === 'group') return { Icon: GroupsIcon, color: '#ca8a04' };
  if (src === 'corporate') return { Icon: BusinessCenterIcon, color: '#0d9488' };
  if (src === 'agent') return { Icon: SupportAgentIcon, color: '#db2777' };
  if (src === 'api') return { Icon: ApiIcon, color: '#64748b' };
  return { Icon: HelpOutlineIcon, color: '#64748b' };
};

const CustomerBookingSourceCell = ({ booking }) => {
  const srcKey = normalizeBookingSourceKey(booking?.booking_source);
  const { Icon, color } = bookingSourceIconAndColor(booking?.booking_source);
  const channelData = booking?.channel_data || null;
  const channelName =
    (channelData?.name && String(channelData.name).trim())
    || (booking?.channel && String(booking.channel).trim())
    || '';
  const label =
    booking?.booking_source_display
    && String(booking.booking_source_display).trim()
    && booking.booking_source_display !== '—'
      ? String(booking.booking_source_display).trim()
      : formatBookingSourceLabel(booking?.booking_source);
  const showOtaChannelImage =
    srcKey === 'ota' && channelData != null && channelData.id != null;
  const primaryText = showOtaChannelImage && channelName ? channelName : label;

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
      {showOtaChannelImage ? (
        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }} title={primaryText}>
          <ChannelImage channel={channelData} size={22} />
        </Box>
      ) : (
        <Icon sx={{ fontSize: 22, color, flexShrink: 0 }} titleAccess={primaryText} />
      )}
      <Typography
        variant="body2"
        component="span"
        sx={{ color: 'text.primary', fontWeight: 600 }}
        noWrap
        title={primaryText}
      >
        {primaryText || '—'}
      </Typography>
    </Stack>
  );
};

const formatStayDate = (value) => {
  if (!value || value === '—') return '—';
  const parsed = moment(value);
  return parsed.isValid() ? formatHotelDate(value) : '—';
};

const StayLine = ({ Icon, iconColor, label, date }) => (
  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ lineHeight: 1.2 }}>
    <Icon sx={{ fontSize: 14, color: iconColor, flexShrink: 0, opacity: 0.92 }} />
    <Typography
      component="span"
      variant="caption"
      sx={{ color: 'text.secondary', fontWeight: 700, fontSize: '0.65rem', minWidth: 22 }}
    >
      {label}
    </Typography>
    <Typography component="span" variant="body2" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '0.78rem' }}>
      {date}
    </Typography>
  </Stack>
);

const CustomerBookingStayCell = ({ booking }) => {
  const nights = Number(booking?.nights || 0);
  const hasNights = Number.isFinite(nights) && nights > 0;
  return (
    <Stack spacing={0.25} sx={{ py: 0.15, minWidth: 155 }}>
      <StayLine
        Icon={LoginIcon}
        iconColor="#4ce1b6"
        label="In"
        date={formatStayDate(booking?.check_in_date)}
      />
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <StayLine
          Icon={LogoutIcon}
          iconColor="#60a5fa"
          label="Out"
          date={formatStayDate(booking?.check_out_date)}
        />
        {hasNights ? (
          <Typography
            variant="caption"
            sx={{
              ml: 0.25,
              color: '#4ce1b6',
              fontWeight: 800,
              fontSize: '0.62rem',
              whiteSpace: 'nowrap',
            }}
          >
            · {nights}N
          </Typography>
        ) : null}
      </Stack>
    </Stack>
  );
};

const getDocumentTypeDisplayLabel = (documentType) => {
  const v = String(documentType || '').trim();
  if (!v) return 'ID';
  const map = {
    aadhaar: 'Aadhaar',
    passport: 'Passport',
    voter_id: 'Voter ID',
    driving_license: 'Driving Licence',
    pan: 'PAN',
    government_id: 'Government ID',
    oci: 'OCI',
    pio: 'PIO',
    student_id: 'Student ID',
    company_id: 'Company ID',
    other: 'Document',
  };
  return map[v] || v.replace(/_/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase());
};

const validateIdNumberByDocumentType = (documentType, idNumber) => {
  const docType = String(documentType || '').trim();
  const raw = String(idNumber || '').trim();
  if (!docType && !raw) return '';
  if (!docType && raw) return 'Select document type before entering ID number';
  if (!raw) return 'ID number is required';

  const normalized = raw.replace(/\s+/g, '').toUpperCase();
  if (docType === 'aadhaar') {
    return validateAadhaarIdNumber(normalized);
  }
  if (docType === 'passport') {
    return isValidPassportIdNumber(normalized)
      ? ''
      : 'Passport number must be 5-20 letters/numbers (e.g. A1234567)';
  }
  if (docType === 'voter_id') {
    return /^[A-Z]{3}[0-9]{7}$/.test(normalized)
      ? ''
      : 'Voter ID must be 3 letters followed by 7 digits (e.g. ABC1234567)';
  }
  if (docType === 'pan') {
    return /^[A-Z]{5}[0-9]{4}[A-Z]$/.test(normalized)
      ? ''
      : 'PAN must be 5 letters, 4 digits, and 1 letter (e.g. ABCDE1234F)';
  }
  if (docType === 'driving_license') {
    return /^[A-Z0-9-]{10,20}$/.test(normalized)
      ? ''
      : 'Driving licence number must be 10-20 characters';
  }
  return /^[A-Z0-9][A-Z0-9\-/]{3,29}$/.test(normalized)
    ? ''
    : 'ID number must be 4-30 characters (letters, numbers, -, /)';
};

const getCustomerBookingPaymentChip = (booking) => {
  const status = booking?.payment_status;
  if (status === 'full_payment' || booking?.is_paid) {
    return { label: 'Paid', color: 'success' };
  }
  if (status === 'partial_payment' || booking?.advance_paid) {
    return { label: 'Partial', color: 'warning' };
  }
  return { label: 'Unpaid', color: 'error' };
};

const CustomerDetailPage = () => {
  const { guestId } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const themeMode = isDark ? 'dark' : 'light';
  const pageShellSx = getDashboardPageShellSx(themeMode);
  const surfaceTokens = getDashboardDarkSurfaceTokens(isDark);
  const accent = DASHBOARD_ACCENT;
  const textPrimary = isDark ? '#e2e8f0' : '#0f172a';
  const textSecondary = isDark ? '#94a3b8' : '#64748b';
  const textBody = isDark ? '#cbd5e1' : '#334155';
  const modernPanelCardSx = {
    borderRadius: 2,
    border: `1px solid ${surfaceTokens.surfaceBorder}`,
    background: isDark ? surfaceTokens.surfaceGradient : '#ffffff',
    boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.04)' : 'none',
    overflow: 'hidden',
  };
  const modernPanelHeaderSx = {
    px: 2,
    py: 1.5,
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    borderBottom: `1px solid ${surfaceTokens.surfaceBorder}`,
    background: isDark ? THEME_COLORS.SURFACE_HEADER_DARK : alpha('#000', 0.02),
  };
  const innerPanelSx = {
    borderRadius: 2,
    border: `1px solid ${surfaceTokens.surfaceBorder}`,
    background: isDark ? THEME_COLORS.SURFACE_HEADER_DARK : alpha('#000', 0.02),
  };
  const outlineButtonSx = {
    borderColor: alpha(accent, isDark ? 0.45 : 0.4),
    color: accent,
    fontWeight: 600,
    textTransform: 'none',
    '&:hover': { borderColor: accent, backgroundColor: alpha(accent, isDark ? 0.12 : 0.08) },
  };
  const cancelButtonSx = {
    borderColor: surfaceTokens.surfaceBorder,
    color: textSecondary,
    textTransform: 'none',
    '&:hover': { borderColor: alpha(accent, 0.45), backgroundColor: alpha(accent, 0.08) },
  };
  const primaryButtonSx = {
    backgroundColor: accent,
    color: '#04120e',
    fontWeight: 700,
    textTransform: 'none',
    '&:hover': { backgroundColor: accent, opacity: 0.92 },
  };

  const [loading, setLoading] = useState(true);
  const [customer, setCustomer] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [eventBookings, setEventBookings] = useState([]);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [contactDrawerOpen, setContactDrawerOpen] = useState(false);
  const [contactDrawerMode, setContactDrawerMode] = useState('view');
  const [contactSaving, setContactSaving] = useState(false);
  const [companyDrawerOpen, setCompanyDrawerOpen] = useState(false);
  const [companyDrawerMode, setCompanyDrawerMode] = useState('view');
  const [companySaving, setCompanySaving] = useState(false);
  const [companyRemoveOpen, setCompanyRemoveOpen] = useState(false);
  const [companyRemoving, setCompanyRemoving] = useState(false);
  const [showFullIdNumber, setShowFullIdNumber] = useState(false);
  const [isIdNumberEditing, setIsIdNumberEditing] = useState(false);
  const [idNumberDraft, setIdNumberDraft] = useState('');
  const [idNumberSaving, setIdNumberSaving] = useState(false);
  const [idNumberError, setIdNumberError] = useState(null);
  const [documentCameraUpload, setDocumentCameraUpload] = useState(false);
  const [isDocumentsEditing, setIsDocumentsEditing] = useState(false);
  const [documentSideSaving, setDocumentSideSaving] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [foreignGuestDrawerOpen, setForeignGuestDrawerOpen] = useState(false);
  const [foreignGuestDrawerMode, setForeignGuestDrawerMode] = useState('view');
  const [foreignGuestSaving, setForeignGuestSaving] = useState(false);
  const [foreignGuestRemoveOpen, setForeignGuestRemoveOpen] = useState(false);
  const [foreignGuestRemoving, setForeignGuestRemoving] = useState(false);
  const stayGuestDrawer = useStayGuestDrawer();
  const [stayGuestSaving, setStayGuestSaving] = useState(false);

  const hotelCountry = useMemo(() => {
    const ctx = (typeof window !== 'undefined' && (window.DJANGO_CONTEXT || window.djangoContext)) || {};
    return ctx.selectedHotel?.country || ctx.hotel?.country || '';
  }, []);
  const foreignGuestCopy = useMemo(
    () => getForeignGuestDrawerCopy(hotelCountry),
    [hotelCountry]
  );

  const getInitialIdNumberDraft = useCallback(
    (documentType, idNumber) =>
      isAadhaarDocumentType(documentType)
        ? formatAadhaarDisplay(idNumber)
        : String(idNumber || ''),
    []
  );

  const refreshCustomerDetail = useCallback(async () => {
    if (!guestId) return;
    const data = await getCustomerDetail(guestId);
    const cust = data.customer || {};
    setCustomer({
      ...cust,
      total_bookings: data.total_bookings ?? cust.total_bookings ?? 0,
      total_spent: data.total_spent ?? cust.total_spent ?? 0,
      full_name:
        cust.full_name ||
        cust.name ||
        [cust.first_name, cust.middle_name, cust.last_name].filter(Boolean).join(' ').trim() ||
        'Customer',
    });
    const statusOrder = [
      'confirmed',
      'checked_in',
      'partial_checkin',
      'partial_checkout',
      'checked_out',
      'pending',
      'reserved',
      'cancelled',
      'no_show',
    ];
    const sorted = (data.bookings || []).slice().sort((a, b) => {
      const ia = statusOrder.indexOf((a.status || '').toLowerCase());
      const ib = statusOrder.indexOf((b.status || '').toLowerCase());
      const ra = ia === -1 ? 99 : ia;
      const rb = ib === -1 ? 99 : ib;
      return ra !== rb ? ra - rb : 0;
    });
    setBookings(sorted);
    setEventBookings(data.event_bookings || []);
  }, [guestId]);

  const paginatedBookings = useMemo(() => {
    const start = page * rowsPerPage;
    return bookings.slice(start, start + rowsPerPage);
  }, [bookings, page, rowsPerPage]);

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(bookings.length / rowsPerPage) - 1);
    if (page > maxPage) setPage(maxPage);
  }, [bookings.length, rowsPerPage, page]);

  const saveStayGuestFromDrawer = useCallback(
    async (payload) => {
      const bid = stayGuestDrawer.bookingId;
      const gid = stayGuestDrawer.guest?.id;
      if (!bid || !gid) return;
      setStayGuestSaving(true);
      try {
        await updateBookingStayGuest(bid, { id: gid, ...payload });
        clearCachedBookingGuestDocuments(bid, gid);
        await refreshCustomerDetail();
        stayGuestDrawer.close();
        setSnackbar({
          open: true,
          message: 'Stay guest info updated for this booking.',
          severity: 'success',
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message: err?.response?.data?.error || err?.message || 'Failed to update stay guest info.',
          severity: 'error',
        });
        throw err;
      } finally {
        setStayGuestSaving(false);
      }
    },
    [refreshCustomerDetail, stayGuestDrawer.bookingId, stayGuestDrawer.close, stayGuestDrawer.guest?.id]
  );

  const handleDocumentUpload = useCallback(async (side, dataUrl) => {
    if (!guestId) return;
    setDocumentSideSaving(side);
    try {
      await updateCustomer(guestId, side === 'front' ? { id_front: dataUrl } : { id_back: dataUrl });
      await refreshCustomerDetail();
      setSnackbar({ open: true, message: `${side === 'front' ? 'Front' : 'Back'} document updated.`, severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || err.message || 'Failed to update document.',
        severity: 'error',
      });
      throw err;
    } finally {
      setDocumentSideSaving(null);
    }
  }, [guestId, refreshCustomerDetail]);

  const handleDocumentRemove = useCallback(async (side) => {
    if (!guestId) return;
    setDocumentSideSaving(side);
    try {
      await updateCustomer(guestId, side === 'front' ? { id_front: '' } : { id_back: '' });
      await refreshCustomerDetail();
      setSnackbar({ open: true, message: `${side === 'front' ? 'Front' : 'Back'} document removed.`, severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || err.message || 'Failed to remove document.',
        severity: 'error',
      });
      throw err;
    } finally {
      setDocumentSideSaving(null);
    }
  }, [guestId, refreshCustomerDetail]);

  const foreignGuestDetails = useMemo(
    () => mapGuestApiToForeignDetails(customer || {}, customer?.country),
    [customer]
  );
  const isForeignGuest = useMemo(
    () => isForeignGuestCountry(customer?.country, hotelCountry),
    [customer?.country, hotelCountry]
  );
  const hasForeignGuestDetails = useMemo(
    () => hasForeignGuestDetailsValues(foreignGuestDetails),
    [foreignGuestDetails]
  );
  const showForeignGuestSection = isForeignGuest || hasForeignGuestDetails;

  const saveForeignGuestDetails = useCallback(
    async (nextDetails) => {
      if (!guestId) return;
      setForeignGuestSaving(true);
      try {
        const payload = foreignDetailsToGuestApiPayload(nextDetails);
        // Drop undefined keys so PATCH does not wipe unrelated fields unintentionally.
        Object.keys(payload).forEach((key) => {
          if (payload[key] === undefined) delete payload[key];
        });
        await updateCustomer(guestId, payload);
        await refreshCustomerDetail();
        setForeignGuestDrawerOpen(false);
        setSnackbar({
          open: true,
          message: 'Foreign guest details saved.',
          severity: 'success',
        });
      } catch (err) {
        setSnackbar({
          open: true,
          message: err.response?.data?.error || err.message || 'Failed to save foreign guest details.',
          severity: 'error',
        });
      } finally {
        setForeignGuestSaving(false);
      }
    },
    [guestId, refreshCustomerDetail]
  );

  const removeForeignGuestDetails = useCallback(async () => {
    if (!guestId) return;
    setForeignGuestRemoving(true);
    try {
      await updateCustomer(guestId, clearForeignGuestApiPayload());
      await refreshCustomerDetail();
      setForeignGuestRemoveOpen(false);
      setSnackbar({
        open: true,
        message: 'Foreign guest details removed.',
        severity: 'success',
      });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || err.message || 'Failed to remove foreign guest details.',
        severity: 'error',
      });
    } finally {
      setForeignGuestRemoving(false);
    }
  }, [guestId, refreshCustomerDetail]);

  const openContactDrawer = useCallback((mode = 'view') => {
    setContactDrawerMode(mode);
    setContactDrawerOpen(true);
  }, []);

  const saveContactFromDrawer = useCallback(
    async (payload) => {
      if (!guestId) return;
      setContactSaving(true);
      try {
        await updateCustomer(guestId, payload);
        await refreshCustomerDetail();
        setContactDrawerOpen(false);
        setSnackbar({ open: true, message: 'Guest info updated.', severity: 'success' });
      } catch (err) {
        setSnackbar({
          open: true,
          message: err.response?.data?.error || err.message || 'Failed to update guest info.',
          severity: 'error',
        });
        throw err;
      } finally {
        setContactSaving(false);
      }
    },
    [guestId, refreshCustomerDetail]
  );
  const openCompanyDrawer = useCallback((mode = 'view') => {
    setCompanyDrawerMode(mode);
    setCompanyDrawerOpen(true);
  }, []);

  const saveCompanyFromDrawer = useCallback(
    async (payload) => {
      if (!guestId) return;
      setCompanySaving(true);
      try {
        await updateCustomer(guestId, payload);
        await refreshCustomerDetail();
        setCompanyDrawerOpen(false);
        setSnackbar({ open: true, message: 'Company details updated.', severity: 'success' });
      } catch (err) {
        setSnackbar({
          open: true,
          message: err.response?.data?.error || err.message || 'Failed to update company details.',
          severity: 'error',
        });
        throw err;
      } finally {
        setCompanySaving(false);
      }
    },
    [guestId, refreshCustomerDetail]
  );

  const removeCompanyDetails = useCallback(async () => {
    if (!guestId) return;
    setCompanyRemoving(true);
    try {
      await updateCustomer(guestId, clearCompanyDetailsApiPayload());
      await refreshCustomerDetail();
      setCompanyRemoveOpen(false);
      setSnackbar({ open: true, message: 'Company details removed.', severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.error || err.message || 'Failed to remove company details.',
        severity: 'error',
      });
    } finally {
      setCompanyRemoving(false);
    }
  }, [guestId, refreshCustomerDetail]);

  useEffect(() => {
    const fetchCustomerDetail = async () => {
      if (!guestId) return;
      setLoading(true);
      setError(null);
      try {
        await refreshCustomerDetail();
        setPage(0);
      } catch (err) {
        setError(err.message || 'Failed to load customer details');
      } finally {
        setLoading(false);
      }
    };
    fetchCustomerDetail();
  }, [guestId, refreshCustomerDetail]);

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    setIdNumberDraft(getInitialIdNumberDraft(customer?.document_type, customer?.id_number));
    setShowFullIdNumber(false);
    setIsIdNumberEditing(false);
    setIdNumberError(null);
  }, [customer?.id, customer?.id_number, customer?.document_type, getInitialIdNumberDraft]);

  const startIdNumberEdit = useCallback(() => {
    if (!customer?.document_type) {
      setIdNumberError('Select a document type first in profile details.');
      return;
    }
    setIdNumberDraft(getInitialIdNumberDraft(customer?.document_type, customer?.id_number));
    setIdNumberError(null);
    setIsIdNumberEditing(true);
  }, [customer, getInitialIdNumberDraft]);

  const cancelIdNumberEdit = useCallback(() => {
    setIdNumberDraft(getInitialIdNumberDraft(customer?.document_type, customer?.id_number));
    setIdNumberError(null);
    setIsIdNumberEditing(false);
  }, [customer?.document_type, customer?.id_number, getInitialIdNumberDraft]);

  const saveIdNumber = useCallback(async () => {
    if (!guestId || !customer) return;
    const validationMessage = validateIdNumberByDocumentType(
      customer.document_type,
      idNumberDraft
    );
    if (validationMessage) {
      setIdNumberError(validationMessage);
      return;
    }
    setIdNumberSaving(true);
    setIdNumberError(null);
    try {
      await updateCustomer(guestId, {
        document_type: (customer.document_type || '').trim() || null,
        id_number:
          normalizeDocumentIdForSubmit(customer.document_type, idNumberDraft) || null,
      });
      await refreshCustomerDetail();
      setIsIdNumberEditing(false);
    } catch (err) {
      setIdNumberError(
        err.response?.data?.error ||
          err.response?.data?.details?.id_number?.[0] ||
          err.message ||
          'Failed to update ID number'
      );
    } finally {
      setIdNumberSaving(false);
    }
  }, [guestId, customer, idNumberDraft, refreshCustomerDetail]);

  const formatCurrency = (amount, currency = 'INR') => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return moment(dateString).format('DD MMM YYYY');
  };

  const getEventPaymentChip = (eventBooking) => {
    const status = String(eventBooking?.payment_status || 'pending').toLowerCase();
    if (status === 'paid') return { label: 'Paid', color: 'success' };
    if (status === 'partial') return { label: 'Partial', color: 'warning' };
    if (status === 'due') return { label: 'Due', color: 'warning' };
    if (status === 'cancelled') return { label: 'Cancelled', color: 'default' };
    return { label: 'Pending', color: 'info' };
  };

  const handleEventBookingClick = (eventId) => {
    const resolvedId = Number(eventId);
    if (!Number.isInteger(resolvedId) || resolvedId <= 0) return;
    navigate(`/hall/event/${resolvedId}/`);
  };

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s.includes('confirmed')) return 'success';
    if (s.includes('tentative')) return 'info';
    if (s.includes('checked in') || s.includes('staying')) return 'info';
    if (s.includes('checked out')) return 'default';
    if (s.includes('cancelled')) return 'error';
    return 'default';
  };

  const handleBookingClick = (bookingId) => {
    const safeBookingId = Number(bookingId);
    if (!Number.isInteger(safeBookingId) || safeBookingId <= 0) {
      setError('Invalid booking reference. Please refresh and try again.');
      return;
    }
    navigate(`/booking/detail/${safeBookingId}/`);
  };

  if (loading) {
    return (
      <Box sx={{ ...pageShellSx, width: '100%', minHeight: '100%' }}>
        <DashboardHeader
          title="Customer Detail"
          backFallbackPath="/customerhub/customers/"
        />
        {/* Hero skeleton */}
        <Paper elevation={0} sx={{ ...modernPanelCardSx, mb: 3, p: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }}>
            <Skeleton variant="circular" width={72} height={72} sx={{ flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Skeleton variant="text" width="70%" height={36} sx={{ mb: 1 }} />
              <Skeleton variant="text" width={180} height={20} sx={{ mb: 1 }} />
              <Skeleton variant="rectangular" width={100} height={28} sx={{ borderRadius: 2 }} />
            </Box>
            <Box sx={{ display: 'flex', gap: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Skeleton variant="text" width={48} height={40} sx={{ mx: 'auto' }} />
                <Skeleton variant="text" width={90} height={20} />
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Skeleton variant="text" width={80} height={32} sx={{ mx: 'auto' }} />
                <Skeleton variant="text" width={70} height={20} />
              </Box>
            </Box>
          </Box>
        </Paper>
        <Grid container spacing={3}>
          {/* Left column skeleton */}
          <Grid item xs={12} md={4}>
            <Card elevation={0} sx={{ ...modernPanelCardSx, mb: 2 }}>
              <CardContent>
                <Skeleton variant="text" width="50%" height={28} sx={{ mb: 2 }} />
                <Skeleton variant="text" width="90%" height={24} sx={{ mb: 1.5 }} />
                <Skeleton variant="text" width="80%" height={24} sx={{ mb: 1.5 }} />
                <Skeleton variant="text" width="85%" height={24} sx={{ mb: 2 }} />
                <Skeleton variant="text" width="95%" height={48} />
              </CardContent>
            </Card>
            <Card elevation={0} sx={modernPanelCardSx}>
              <CardContent>
                <Skeleton variant="text" width="55%" height={28} sx={{ mb: 2 }} />
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                  <Skeleton variant="rectangular" width="100%" height={100} sx={{ borderRadius: 1, flex: 1 }} />
                  <Skeleton variant="rectangular" width="100%" height={100} sx={{ borderRadius: 1, flex: 1 }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
          {/* Right column skeleton - table */}
          <Grid item xs={12} md={8}>
            <Card elevation={0} sx={modernPanelCardSx}>
              <Box sx={modernPanelHeaderSx}>
                <Skeleton variant="text" width={180} height={32} />
              </Box>
              <TableContainer>
                <Table size="medium">
                  <TableHead>
                    <TableRow>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                        <TableCell key={i}><Skeleton variant="text" width={i === 1 ? 90 : 70} height={24} /></TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[1, 2, 3, 4, 5, 6].map((row) => (
                      <TableRow key={row}>
                        <TableCell><Skeleton variant="text" width={80} height={22} /></TableCell>
                        <TableCell><Skeleton variant="rectangular" width={72} height={24} sx={{ borderRadius: 1 }} /></TableCell>
                        <TableCell><Skeleton variant="text" width={88} height={22} /></TableCell>
                        <TableCell><Skeleton variant="text" width={88} height={22} /></TableCell>
                        <TableCell><Skeleton variant="text" width={70} height={22} /></TableCell>
                        <TableCell><Skeleton variant="text" width={56} height={22} /></TableCell>
                        <TableCell><Skeleton variant="text" width={64} height={22} /></TableCell>
                        <TableCell><Skeleton variant="rectangular" width={56} height={24} sx={{ borderRadius: 1 }} /></TableCell>
                        <TableCell><Skeleton variant="circular" width={32} height={32} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: 'divider' }}>
                <Skeleton variant="text" width={200} height={40} />
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  }

  if (error || !customer) {
    return (
      <Box sx={pageShellSx}>
        <DashboardHeader
          title="Customer Detail"
          backFallbackPath="/customerhub/customers/"
        />
        <Paper sx={{ ...modernPanelCardSx, p: 3, textAlign: 'center' }}>
          <Typography color="error">{error || 'Customer not found'}</Typography>
        </Paper>
      </Box>
    );
  }

  const isLoyaltyMember = customer.loyalty_member_no && customer.loyalty_member_no !== '—' && String(customer.loyalty_member_no).trim() !== '';
  const hasCompanyDetails = Boolean(
    customer.company_name ||
    customer.company_tax_id_type ||
    customer.company_tax_id_number ||
    customer.company_phone ||
    customer.company_email ||
    customer.company_address ||
    customer.company_city ||
    customer.company_state ||
    customer.company_country ||
    customer.company_pincode ||
    customer.company_postal_code
  );
  const companyAddressLine = (customer.company_address || '').trim();
  const companyLocationLine = [customer.company_city, customer.company_state, customer.company_country ? formatCountryLabel(customer.company_country) : '']
    .map((v) => (v || '').trim())
    .filter(Boolean)
    .join(', ');
  const companyPinValues = Array.from(
    new Set(
      [customer.company_pincode, customer.company_postal_code]
        .map((v) => (v || '').trim())
        .filter(Boolean)
    )
  );
  const companyPinDisplay = companyPinValues.join(' / ');
  // VIP tier from booking count: >10 VVVIP, >=7 VVIP, >=4 Gold VIP, >=2 Gold
  const getVipTier = (count) => {
    const n = Number(count) || 0;
    if (n > 10) return { label: 'VVVIP', icon: DiamondIcon, color: '#9333ea', bg: 'linear-gradient(135deg, #c084fc 0%, #7c3aed 50%, #4c1d95 100%)', shadow: '0 4px 14px rgba(147,51,234,0.45)' };
    if (n >= 7) return { label: 'VVIP', icon: WorkspacePremiumIcon, color: '#ca8a04', bg: 'linear-gradient(135deg, #fde047 0%, #eab308 50%, #a16207 100%)', shadow: '0 4px 14px rgba(202,138,4,0.4)' };
    if (n >= 4) return { label: 'Gold VIP', icon: StarIcon, color: '#ea580c', bg: 'linear-gradient(135deg, #fb923c 0%, #ea580c 50%, #c2410c 100%)', shadow: '0 4px 14px rgba(234,88,12,0.35)' };
    if (n >= 2) return { label: 'Gold', icon: StarIcon, color: '#b45309', bg: 'linear-gradient(135deg, #fdba74 0%, #f59e0b 50%, #d97706 100%)', shadow: '0 4px 14px rgba(245,158,11,0.35)' };
    return null;
  };
  const vipTier = getVipTier(customer.total_bookings ?? 0);
  const idNumberFieldLabel = `${getDocumentTypeDisplayLabel(customer.document_type)} Number`;
  const isAadhaarDocument = isAadhaarDocumentType(customer?.document_type);
  const documentTypeLabel = getDocumentTypeDisplayLabel(customer.document_type);
  const frontDocumentLabel = `${documentTypeLabel} Front`;
  const backDocumentLabel = `${documentTypeLabel} Back`;
  const normalizedIdNumberDraft = isAadhaarDocument
    ? formatAadhaarDisplay(idNumberDraft)
    : idNumberDraft;

  return (
    <Box sx={{ ...pageShellSx, width: '100%', boxSizing: 'border-box' }}>
      <DashboardHeader
        title="Customer Detail"
        backFallbackPath="/customerhub/customers/"
      />

      {/* Hero Card */}
      <Paper
        elevation={0}
        sx={{
          ...modernPanelCardSx,
          mb: 3,
        }}
      >
        <Box sx={{ p: { xs: 2, sm: 2.1 } }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: { xs: 'stretch', md: 'center' },
              gap: { xs: 2, md: 3 },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: { xs: 1.5, sm: 2 }, flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  width: { xs: 56, sm: 72 },
                  height: { xs: 56, sm: 72 },
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  background: isDark
                    ? `linear-gradient(135deg, ${alpha(accent, 0.18)} 0%, ${alpha(accent, 0.08)} 100%)`
                    : `linear-gradient(135deg, ${alpha(accent, 0.14)} 0%, ${alpha(accent, 0.06)} 100%)`,
                  boxShadow: isDark
                    ? `0 8px 18px ${alpha('#000', 0.28)}`
                    : `0 8px 18px ${alpha(accent, 0.14)}`,
                  border: `2px solid ${alpha(accent, isDark ? 0.28 : 0.35)}`,
                }}
              >
                <PersonIcon sx={{ fontSize: { xs: 28, sm: 34 }, color: accent }} />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.5 }}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontWeight: 800,
                      color: textPrimary,
                      fontSize: { xs: '1.125rem', sm: '1.5rem' },
                      lineHeight: 1.25,
                      wordBreak: 'break-word',
                    }}
                  >
                    {customer.full_name || customer.name || [customer.first_name, customer.middle_name, customer.last_name].filter(Boolean).join(' ').trim() || 'Customer'}
                  </Typography>
                  {(customer.is_child || (customer.dob && moment().diff(moment(customer.dob), 'years') < 18)) && (
                    <Chip
                      icon={<ChildCareIcon sx={{ fontSize: '16px !important' }} />}
                      label="Child"
                      size="small"
                      sx={{
                        height: 24,
                        fontWeight: 700,
                        fontSize: '0.72rem',
                        bgcolor: alpha('#1565c0', isDark ? 0.25 : 0.12),
                        color: isDark ? '#90caf9' : '#1565c0',
                        border: `1px solid ${alpha('#1565c0', isDark ? 0.45 : 0.3)}`,
                        '& .MuiChip-icon': { color: 'inherit' },
                      }}
                    />
                  )}
                  {isForeignGuest && (
                    <Chip
                      icon={<PublicIcon sx={{ fontSize: '16px !important' }} />}
                      label="Foreign"
                      size="small"
                      sx={getForeignGuestChipSx(isDark, { height: 24, fontSize: '0.72rem' })}
                    />
                  )}
                  {vipTier && (() => {
                    const TierIcon = vipTier.icon;
                    return (
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.75,
                          px: 1.25,
                          py: 0.45,
                          borderRadius: 2,
                          background: vipTier.bg,
                          color: '#fff',
                          fontWeight: 700,
                          fontSize: { xs: '0.68rem', sm: '0.8rem' },
                          letterSpacing: '0.04em',
                          boxShadow: vipTier.shadow,
                          border: '1px solid rgba(255,255,255,0.35)',
                          textTransform: 'uppercase',
                          flexShrink: 0,
                        }}
                      >
                        <TierIcon sx={{ fontSize: { xs: 14, sm: 18 } }} />
                        {vipTier.label}
                      </Box>
                    );
                  })()}
                </Box>
                {isLoyaltyMember && (
                  <Box>
                    <Chip
                      icon={<CardMembershipIcon sx={{ fontSize: 12, color: '#000 !important' }} />}
                      label={`Loyalty ID: ${customer.loyalty_member_no}`}
                      size="small"
                      sx={{
                        background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                        color: '#000',
                        fontWeight: 600,
                        fontSize: '0.6875rem',
                        height: 22,
                        borderRadius: '11px',
                        boxShadow: '0 2px 8px rgba(217, 119, 6, 0.35)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        transition: 'all 0.2s ease-in-out',
                        '& .MuiChip-icon': {
                          color: '#000',
                          marginLeft: '6px',
                        },
                        '& .MuiChip-label': {
                          px: 0.5,
                        },
                        '&:hover': {
                          transform: 'translateY(-1px)',
                          boxShadow: '0 2px 8px rgba(217, 119, 6, 0.5), 0 4px 12px rgba(0,0,0,0.1)',
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr 1fr',
                  md: eventBookings.length > 0 ? 'auto auto auto' : 'auto auto',
                },
                gap: { xs: 1.25, sm: 2, md: 3 },
                width: { xs: '100%', md: 'auto' },
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  ...innerPanelSx,
                  px: { xs: 1.25, sm: 2 },
                  py: { xs: 1.25, sm: 1.5 },
                  textAlign: 'center',
                  minWidth: 0,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, mb: 0.5 }}>
                  <ReceiptIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: accent, flexShrink: 0 }} />
                  <Typography
                    sx={{
                      fontWeight: 800,
                      color: textPrimary,
                      fontSize: { xs: '1.35rem', sm: '2.125rem' },
                      lineHeight: 1.1,
                    }}
                  >
                    {customer.total_bookings ?? 0}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: textSecondary, fontWeight: 700, fontSize: { xs: '0.68rem', sm: '0.75rem' } }}>
                  Total Bookings
                </Typography>
                {(customer.total_event_bookings > 0 || customer.total_room_bookings > 0) ? (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.35, color: textSecondary, fontWeight: 600, fontSize: { xs: '0.62rem', sm: '0.68rem' } }}>
                    {[
                      customer.total_room_bookings > 0 ? `${customer.total_room_bookings} room` : null,
                      customer.total_event_bookings > 0 ? `${customer.total_event_bookings} event` : null,
                    ].filter(Boolean).join(' · ')}
                  </Typography>
                ) : null}
              </Box>
              <Box
                sx={{
                  ...innerPanelSx,
                  px: { xs: 1.25, sm: 2 },
                  py: { xs: 1.25, sm: 1.5 },
                  textAlign: 'center',
                  minWidth: 0,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, mb: 0.5, minWidth: 0 }}>
                  <PaidIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: accent, flexShrink: 0 }} />
                  <Typography
                    sx={{
                      fontWeight: 800,
                      color: textPrimary,
                      fontSize: { xs: '0.95rem', sm: '1.25rem' },
                      lineHeight: 1.2,
                      wordBreak: 'break-word',
                    }}
                  >
                    {formatCurrency(customer.total_spent || 0, customer.currency)}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: textSecondary, fontWeight: 700, fontSize: { xs: '0.68rem', sm: '0.75rem' } }}>
                  Total Spent
                </Typography>
                {customer.total_event_spent > 0 ? (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.35, color: textSecondary, fontWeight: 600, fontSize: { xs: '0.62rem', sm: '0.68rem' } }}>
                    Incl. {formatCurrency(customer.total_event_spent, customer.currency)} events
                  </Typography>
                ) : null}
              </Box>
              {eventBookings.length > 0 ? (
                <Box
                  sx={{
                    ...innerPanelSx,
                    px: { xs: 1.25, sm: 2 },
                    py: { xs: 1.25, sm: 1.5 },
                    textAlign: 'center',
                    minWidth: 0,
                    gridColumn: { xs: '1 / -1', md: 'auto' },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.75, mb: 0.5 }}>
                    <EventIcon sx={{ fontSize: { xs: 18, sm: 20 }, color: accent, flexShrink: 0 }} />
                    <Typography
                      sx={{
                        fontWeight: 800,
                        color: textPrimary,
                        fontSize: { xs: '1.35rem', sm: '2.125rem' },
                        lineHeight: 1.1,
                      }}
                    >
                      {eventBookings.length}
                    </Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: textSecondary, fontWeight: 700, fontSize: { xs: '0.68rem', sm: '0.75rem' } }}>
                    Event Bookings
                  </Typography>
                </Box>
              ) : null}
            </Box>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* Left: Contact & Info */}
        <Grid item xs={12} md={4}>
          <Card elevation={0} sx={{ ...modernPanelCardSx, mb: 2 }}>
            <CardContent sx={{ '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, color: textPrimary, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon sx={{ color: accent, fontSize: 22 }} />
                  Contact & Info
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<VisibilityIcon />}
                  onClick={() => openContactDrawer('view')}
                  sx={outlineButtonSx}
                >
                  View info
                </Button>
              </Box>

              {customer.email && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <EmailIcon sx={{ fontSize: 20, color: accent }} />
                  <Typography variant="body2" sx={{ color: textBody }}>{customer.email}</Typography>
                </Box>
              )}
              {(customer.phone || customer.country_code) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
                  <PhoneIcon sx={{ fontSize: 20, color: accent }} />
                  <Typography variant="body2" sx={{ color: textBody, flex: 1, minWidth: 0 }}>
                    {[customer.country_code, customer.phone].filter(Boolean).join(' ').trim() || '—'}
                  </Typography>
                </Box>
              )}
              {customer.alternate_phone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <PhoneIcon sx={{ fontSize: 20, color: accent }} />
                  <Typography variant="body2" sx={{ color: textBody }}>{customer.alternate_phone} (Alt)</Typography>
                </Box>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                <PersonIcon sx={{ fontSize: 20, color: accent }} />
                <Typography variant="body2" sx={{ color: textBody }}>
                  Gender: {customer.gender ? String(customer.gender).charAt(0).toUpperCase() + String(customer.gender).slice(1).toLowerCase() : '—'}
                </Typography>
              </Box>
              {customer.dob && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
                  <CalendarIcon sx={{ fontSize: 20, color: accent }} />
                  <Typography variant="body2" sx={{ color: textBody }}>DOB: {formatDate(customer.dob)}</Typography>
                </Box>
              )}
              {(customer.address_line1 || customer.address_line2 || customer.city || customer.state || customer.country || customer.pincode) && (
                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${surfaceTokens.surfaceBorder}` }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
                    <LocationIcon sx={{ fontSize: 20, color: accent, mt: 0.25 }} />
                    <Box>
                      {customer.address_line1 && (
                        <Typography variant="body2" sx={{ color: textBody }}>{customer.address_line1}</Typography>
                      )}
                      {customer.address_line2 && (
                        <Typography variant="body2" sx={{ color: textBody }}>{customer.address_line2}</Typography>
                      )}
                      {(customer.city || customer.state || customer.country || customer.pincode) && (
                        <Typography variant="body2" sx={{ color: textBody }}>
                          {[customer.city, customer.state, customer.country ? formatCountryLabel(customer.country) : ''].filter(Boolean).join(', ')}
                          {customer.pincode ? ` - ${customer.pincode}` : ''}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
              {customer.notes && (
                <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${surfaceTokens.surfaceBorder}` }}>
                  <Typography variant="caption" sx={{ color: textSecondary, fontWeight: 600 }}>Notes</Typography>
                  <Typography variant="body2" sx={{ color: textBody, mt: 0.5 }}>{customer.notes}</Typography>
                </Box>
              )}

              <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${surfaceTokens.surfaceBorder}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, flexWrap: 'wrap', gap: 1 }}>
                  <Typography variant="subtitle1" sx={{ color: textPrimary, fontWeight: 800 }}>
                    Company Details
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {hasCompanyDetails ? (
                      <>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<VisibilityIcon />}
                          onClick={() => openCompanyDrawer('view')}
                          disabled={companySaving || companyRemoving}
                          sx={outlineButtonSx}
                        >
                          View info
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => setCompanyRemoveOpen(true)}
                          disabled={companySaving || companyRemoving}
                          sx={{
                            ...cancelButtonSx,
                            borderColor: alpha(theme.palette.error.main, 0.45),
                            color: theme.palette.error.main,
                            '&:hover': {
                              borderColor: theme.palette.error.main,
                              backgroundColor: alpha(theme.palette.error.main, 0.08),
                            },
                          }}
                        >
                          Remove
                        </Button>
                      </>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<VisibilityIcon />}
                        onClick={() => openCompanyDrawer('edit')}
                        disabled={companySaving || companyRemoving}
                        sx={outlineButtonSx}
                      >
                        Add
                      </Button>
                    )}
                  </Box>
                </Box>
                {hasCompanyDetails ? (
                  <Grid container spacing={0.75} sx={{ mt: 0.5 }}>
                    {customer.company_name && (
                      <>
                        <Grid item xs={5}>
                          <Typography variant="body2" sx={{ color: textSecondary, fontWeight: 700 }}>Company</Typography>
                        </Grid>
                        <Grid item xs={7}>
                          <Typography variant="body1" sx={{ color: textPrimary, fontWeight: 500 }}>{customer.company_name}</Typography>
                        </Grid>
                      </>
                    )}
                    {customer.company_tax_id_type && (
                      <>
                        <Grid item xs={5}>
                          <Typography variant="body2" sx={{ color: textSecondary, fontWeight: 700 }}>Tax ID Type</Typography>
                        </Grid>
                        <Grid item xs={7}>
                          <Typography variant="body1" sx={{ color: textPrimary, fontWeight: 500 }}>
                            {String(customer.company_tax_id_type).toUpperCase()}
                          </Typography>
                        </Grid>
                      </>
                    )}
                    {customer.company_phone && (
                      <>
                        <Grid item xs={5}>
                          <Typography variant="body2" sx={{ color: textSecondary, fontWeight: 700 }}>Phone</Typography>
                        </Grid>
                        <Grid item xs={7}>
                          <Typography variant="body1" sx={{ color: textPrimary, fontWeight: 500 }}>{customer.company_phone}</Typography>
                        </Grid>
                      </>
                    )}
                    {companyLocationLine && (
                      <>
                        <Grid item xs={5}>
                          <Typography variant="body2" sx={{ color: textSecondary, fontWeight: 700 }}>Location</Typography>
                        </Grid>
                        <Grid item xs={7}>
                          <Typography variant="body1" sx={{ color: textPrimary, fontWeight: 500 }}>{companyLocationLine}</Typography>
                        </Grid>
                      </>
                    )}
                  </Grid>
                ) : (
                  <Typography variant="body2" sx={{ color: textSecondary }}>
                    No company details added.
                  </Typography>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Foreign Guest Details */}
          {showForeignGuestSection && (
            <Card elevation={0} sx={modernPanelCardSx}>
              <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                    mb: 1,
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 800, color: textPrimary, display: 'flex', alignItems: 'center', gap: 1 }}
                  >
                    <PublicIcon sx={{ color: accent, fontSize: 22 }} />
                    {foreignGuestCopy.title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => {
                        setForeignGuestDrawerMode(hasForeignGuestDetails ? 'view' : 'edit');
                        setForeignGuestDrawerOpen(true);
                      }}
                      disabled={foreignGuestSaving || foreignGuestRemoving}
                      sx={outlineButtonSx}
                    >
                      {hasForeignGuestDetails ? 'View info' : 'Add'}
                    </Button>
                    {hasForeignGuestDetails ? (
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteOutlineIcon />}
                        onClick={() => setForeignGuestRemoveOpen(true)}
                        disabled={foreignGuestSaving || foreignGuestRemoving}
                        sx={{
                          ...cancelButtonSx,
                          borderColor: alpha(theme.palette.error.main, 0.45),
                          color: theme.palette.error.main,
                          '&:hover': {
                            borderColor: theme.palette.error.main,
                            backgroundColor: alpha(theme.palette.error.main, 0.08),
                          },
                        }}
                      >
                        Remove
                      </Button>
                    ) : null}
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ color: textSecondary, mb: 1 }}>
                  {foreignGuestCopy.subtitle}
                </Typography>
                {hasForeignGuestDetails ? (
                  <Typography variant="body2" sx={{ color: textBody, fontWeight: 600 }}>
                    {[
                      foreignGuestDetails.passportNumber
                        ? `Passport ${foreignGuestDetails.passportNumber}`
                        : null,
                      foreignGuestDetails.nationality
                        ? formatCountryLabel(foreignGuestDetails.nationality)
                        : null,
                    ]
                      .filter(Boolean)
                      .join(' · ') || 'Travel documents on file'}
                  </Typography>
                ) : (
                  <Typography variant="body2" sx={{ color: textSecondary }}>
                    No foreign guest details added yet.
                  </Typography>
                )}
              </CardContent>
            </Card>
          )}

          {/* Documents */}
          {(customer.document_type || customer.id_number || customer.id_number_masked || customer.id_front_url || customer.id_back_url) && (
            <Card
              elevation={0}
              sx={modernPanelCardSx}
            >
              <CardContent sx={{ '&:last-child': { pb: 2 } }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 800, mb: 2, color: textPrimary, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DescriptionIcon sx={{ color: accent, fontSize: 22 }} />
                  Identity Documents
                </Typography>
                <Box sx={{ mb: 2, p: 1.5, ...innerPanelSx }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 0.75 }}>
                    <Typography variant="caption" sx={{ color: textSecondary, fontWeight: 700 }}>
                      {idNumberFieldLabel}
                    </Typography>
                    {!isIdNumberEditing ? (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant="text"
                          startIcon={showFullIdNumber ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          onClick={() => setShowFullIdNumber((v) => !v)}
                          disabled={!customer?.id_number && !customer?.id_number_masked}
                          sx={{ textTransform: 'none', minWidth: 0, px: 1 }}
                        >
                          {showFullIdNumber ? 'Show masked' : 'View full'}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<EditIcon />}
                          onClick={startIdNumberEdit}
                          disabled={contactDrawerOpen || companyDrawerOpen || contactSaving || companySaving}
                          sx={outlineButtonSx}
                        >
                          Edit
                        </Button>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<CloseIcon />}
                          onClick={cancelIdNumberEdit}
                          disabled={idNumberSaving}
                          sx={cancelButtonSx}
                        >
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={idNumberSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                          onClick={saveIdNumber}
                          disabled={idNumberSaving}
                          sx={primaryButtonSx}
                        >
                          {idNumberSaving ? 'Saving…' : 'Save'}
                        </Button>
                      </Box>
                    )}
                  </Box>
                  {isIdNumberEditing ? (
                    <TextField
                      fullWidth
                      size="small"
                      placeholder={
                        isAadhaarDocument
                          ? 'XXXX-XXXX-XXXX'
                          : `Enter ${idNumberFieldLabel.toLowerCase()}`
                      }
                      value={normalizedIdNumberDraft}
                      onChange={(e) => {
                        setIdNumberDraft(normalizeDocumentIdInput(customer?.document_type, e.target.value));
                        if (idNumberError) setIdNumberError(null);
                      }}
                      error={!!idNumberError}
                      helperText={idNumberError || ''}
                      inputProps={{
                        maxLength: isAadhaarDocument ? 14 : undefined,
                        inputMode: isAadhaarDocument ? 'numeric' : undefined,
                        pattern: isAadhaarDocument ? '[0-9\\-]*' : undefined,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 1.5,
                          '& fieldset': { borderColor: alpha(accent, 0.3) },
                          '&:hover fieldset': { borderColor: accent },
                        },
                      }}
                    />
                  ) : (
                    <Typography variant="body2" sx={{ color: textPrimary, fontWeight: 600, letterSpacing: 0.3 }}>
                      {showFullIdNumber
                        ? (isAadhaarDocument
                          ? (formatAadhaarDisplay(customer?.id_number) || '—')
                          : ((customer?.id_number || '').trim() || '—'))
                        : ((customer?.id_number_masked || customer?.id_number || '').trim() || '—')}
                    </Typography>
                  )}
                  {!isIdNumberEditing && idNumberError ? (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                      {idNumberError}
                    </Typography>
                  ) : null}
                </Box>

                <Box sx={{ p: 1.5, ...innerPanelSx }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'wrap',
                      mb: isDocumentsEditing ? 1.25 : 1.5,
                    }}
                  >
                    <Box>
                      <Typography variant="caption" sx={{ color: textSecondary, fontWeight: 700, display: 'block' }}>
                        Document scans
                      </Typography>
                      <Typography variant="caption" sx={{ color: textSecondary, opacity: 0.85 }}>
                        {isDocumentsEditing ? 'Replace, remove, or upload new images' : 'Click a scan to view full size'}
                      </Typography>
                    </Box>
                    {!isDocumentsEditing ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => setIsDocumentsEditing(true)}
                        disabled={contactDrawerOpen || companyDrawerOpen || Boolean(documentSideSaving)}
                        sx={outlineButtonSx}
                      >
                        Edit
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CloseIcon />}
                        onClick={() => {
                          setIsDocumentsEditing(false);
                          setDocumentCameraUpload(false);
                        }}
                        disabled={Boolean(documentSideSaving)}
                        sx={cancelButtonSx}
                      >
                        Done
                      </Button>
                    )}
                  </Box>

                  {isDocumentsEditing ? (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.25 }}>
                      <FormControlLabel
                        label="Camera upload"
                        labelPlacement="start"
                        control={
                          <Switch
                            checked={documentCameraUpload}
                            onChange={(e) => setDocumentCameraUpload(e.target.checked)}
                            color="primary"
                            disabled={Boolean(documentSideSaving)}
                          />
                        }
                        sx={{ color: textSecondary, mr: 0 }}
                      />
                    </Box>
                  ) : null}

                  <Grid container spacing={1.5}>
                    <Grid item xs={6}>
                      <GuestDocumentSideEditor
                        label={frontDocumentLabel}
                        imageUrl={customer.id_front_url || ''}
                        isDark={isDark}
                        accent={accent}
                        isEditing={isDocumentsEditing}
                        cameraUpload={documentCameraUpload}
                        disabled={contactDrawerOpen || companyDrawerOpen || contactSaving || companySaving}
                        saving={documentSideSaving === 'front'}
                        onUpload={(dataUrl) => handleDocumentUpload('front', dataUrl)}
                        onRemove={() => handleDocumentRemove('front')}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      <GuestDocumentSideEditor
                        label={backDocumentLabel}
                        imageUrl={customer.id_back_url || ''}
                        isDark={isDark}
                        accent={accent}
                        isEditing={isDocumentsEditing}
                        cameraUpload={documentCameraUpload}
                        disabled={contactDrawerOpen || companyDrawerOpen || contactSaving || companySaving}
                        saving={documentSideSaving === 'back'}
                        onUpload={(dataUrl) => handleDocumentUpload('back', dataUrl)}
                        onRemove={() => handleDocumentRemove('back')}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </CardContent>
            </Card>
          )}
        </Grid>

        {/* Right: Bookings */}
        <Grid item xs={12} md={8}>
          <Card
            elevation={0}
            sx={modernPanelCardSx}
          >
            <Box sx={modernPanelHeaderSx}>
              <EventAvailableIcon sx={{ color: accent, fontSize: 24 }} />
              <Typography variant="h6" sx={{ fontWeight: 800, color: textPrimary }}>
                Booking History
              </Typography>
            </Box>
            <TableContainer>
              <Table size="medium">
                <TableHead>
                  <TableRow sx={{ backgroundColor: isDark ? THEME_COLORS.SURFACE_HEADER_DARK : alpha('#f1f5f9', 0.95) }}>
                    <TableCell sx={{ fontWeight: 700, color: textPrimary, borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }}>Booking</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: textPrimary, borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }}>Role</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: textPrimary, borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }}>Room / Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: textPrimary, borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }}>Stay guest</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: textPrimary, borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }}>Stay</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: textPrimary, borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }}>Source</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: textPrimary, borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: textPrimary, borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }}>Payment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bookings.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} align="center" sx={{ py: 4, color: textSecondary }}>
                        No bookings found
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedBookings.map((booking) => {
                      const resolvedBookingId = Number(booking?.id || booking?.booking_id);
                      const safeBookingId =
                        Number.isInteger(resolvedBookingId) && resolvedBookingId > 0
                          ? resolvedBookingId
                          : null;
                      const paymentChip = getCustomerBookingPaymentChip(booking);
                      const stayGuest = booking?.stay_guest || null;
                      const stayGuestName =
                        booking?.stay_guest_name ||
                        stayGuest?.name ||
                        [stayGuest?.first_name, stayGuest?.last_name].filter(Boolean).join(' ').trim() ||
                        '—';
                      const hasStaySnapshot = Boolean(booking?.has_stay_guest_snapshot || stayGuest?.snapshot_id);
                      const guestRoleLabel =
                        booking?.guest_role_display
                        || (booking?.is_primary_guest || booking?.guest_role === 'primary'
                          ? 'Primary'
                          : 'Co-guest');
                      const isPrimaryRole = Boolean(
                        booking?.is_primary_guest || booking?.guest_role === 'primary',
                      );
                      const roomLabel =
                        booking?.room_label
                        || (Array.isArray(booking?.rooms)
                          ? booking.rooms
                              .map((r) => r?.room_number || r?.label)
                              .filter(Boolean)
                              .filter((v, i, arr) => arr.indexOf(v) === i)
                              .join(', ')
                          : '')
                        || '—';
                      return (
                      <TableRow
                        key={safeBookingId || `${booking?.code || 'booking'}_${booking?.check_in_date || ''}_${booking?.check_out_date || ''}`}
                        hover
                        onClick={() => handleBookingClick(safeBookingId)}
                        sx={{
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: isDark ? alpha(accent, 0.08) : alpha(accent, 0.06),
                          },
                        }}
                      >
                        <TableCell sx={{ fontWeight: 600, color: accent }}>
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                            <Typography component="span" sx={{ fontWeight: 700, color: accent, fontSize: '0.875rem' }}>
                              {booking.code || '—'}
                            </Typography>
                            {safeBookingId ? (
                              <Typography component="span" sx={{ color: textSecondary, fontSize: '0.75rem', fontWeight: 500 }}>
                                ID {safeBookingId}
                              </Typography>
                            ) : null}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={guestRoleLabel}
                            size="small"
                            color={isPrimaryRole ? 'primary' : 'default'}
                            variant={isPrimaryRole ? 'filled' : 'outlined'}
                            sx={{ fontWeight: 700 }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: textBody, minWidth: 120 }}>
                          <Stack spacing={0.5} alignItems="flex-start">
                            <Typography
                              component="span"
                              sx={{ fontWeight: 700, color: textPrimary, fontSize: '0.875rem', lineHeight: 1.2 }}
                            >
                              {roomLabel}
                            </Typography>
                            <Chip
                              label={String(booking.status || '—').replace(/_/g, ' ')}
                              color={getStatusColor(booking.status)}
                              size="small"
                              sx={{ height: 22, fontWeight: 700, textTransform: 'capitalize' }}
                            />
                          </Stack>
                        </TableCell>
                        <TableCell
                          onClick={(e) => e.stopPropagation()}
                          sx={{ color: textBody, minWidth: 140 }}
                        >
                          {stayGuest ? (
                            <Button
                              size="small"
                              variant="text"
                              startIcon={<PersonSearchIcon sx={{ fontSize: 16 }} />}
                              onClick={() => {
                                stayGuestDrawer.openStayGuest({
                                  stayGuest,
                                  bookingId: safeBookingId,
                                  bookingCode: booking.code || '',
                                });
                              }}
                              onMouseEnter={() =>
                                stayGuestDrawer.prefetchStayGuestDocs(stayGuest, safeBookingId)
                              }
                              sx={{
                                color: accent,
                                fontWeight: 700,
                                textTransform: 'none',
                                px: 0.5,
                                minWidth: 0,
                                justifyContent: 'flex-start',
                                '&:hover': {
                                  backgroundColor: alpha(accent, isDark ? 0.12 : 0.08),
                                },
                              }}
                            >
                              {stayGuestName}
                              {hasStaySnapshot ? '' : ' (live)'}
                            </Button>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                        <TableCell sx={{ color: textBody }}>
                          <CustomerBookingStayCell booking={booking} />
                        </TableCell>
                        <TableCell sx={{ color: textBody }}>
                          <CustomerBookingSourceCell booking={booking} />
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, color: textPrimary }}>
                          {formatCurrency(booking.total_amount, booking.currency)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={paymentChip.label}
                            color={paymentChip.color}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={bookings.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                borderTop: `1px solid ${surfaceTokens.surfaceBorder}`,
                color: textSecondary,
              }}
            />
          </Card>

          {eventBookings.length > 0 ? (
            <Card
              elevation={0}
              sx={{ ...modernPanelCardSx, mt: 2.5 }}
            >
              <Box sx={modernPanelHeaderSx}>
                <EventIcon sx={{ color: accent, fontSize: 24 }} />
                <Typography variant="h6" sx={{ fontWeight: 800, color: textPrimary }}>
                  Event Bookings
                </Typography>
                <Chip
                  size="small"
                  label={`${eventBookings.length}`}
                  sx={{
                    ml: 0.5,
                    height: 22,
                    fontWeight: 800,
                    bgcolor: alpha(accent, isDark ? 0.2 : 0.16),
                    color: isDark ? '#bbf7d0' : '#0f766e',
                  }}
                />
              </Box>
              <TableContainer>
                <Table size="medium">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: isDark ? THEME_COLORS.SURFACE_HEADER_DARK : alpha('#f1f5f9', 0.95) }}>
                      <TableCell sx={{ fontWeight: 700, color: textPrimary, borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }}>Event booking</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: textPrimary, borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }}>Event ID</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: textPrimary, borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }}>Event Name</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: textPrimary, borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }}>Status</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: textPrimary, borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }}>Date</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: textPrimary, borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }}>Time</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: textPrimary, borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }}>Hall</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: textPrimary, borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }}>Amount</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: textPrimary, borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }} align="right">Guests</TableCell>
                      <TableCell sx={{ fontWeight: 700, color: textPrimary, borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }}>Payment</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700, color: textPrimary, borderBottom: `1px solid ${surfaceTokens.surfaceBorder}` }} />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {eventBookings.map((eventBooking, index) => {
                      const resolvedEventId = Number(eventBooking?.id);
                      const safeEventId =
                        Number.isInteger(resolvedEventId) && resolvedEventId > 0
                          ? resolvedEventId
                          : null;
                      const paymentChip = getEventPaymentChip(eventBooking);
                      const eventReference = eventBooking.booking_reference || eventBooking.event_id || '—';
                      const eventLabel = eventBooking.label || `Event booking ${eventBooking.sequence || index + 1}`;
                      return (
                        <TableRow
                          key={safeEventId || `${eventReference}_${eventBooking.event_date || ''}`}
                          hover
                          onClick={() => handleEventBookingClick(safeEventId)}
                          sx={{
                            cursor: safeEventId ? 'pointer' : 'default',
                            '&:hover': safeEventId ? {
                              backgroundColor: isDark ? alpha(accent, 0.08) : alpha(accent, 0.06),
                            } : undefined,
                          }}
                        >
                          <TableCell sx={{ fontWeight: 800, color: accent }}>
                            {eventLabel}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: textPrimary }}>
                            {eventReference}
                          </TableCell>
                          <TableCell sx={{ color: textPrimary, fontWeight: 600 }}>
                            {eventBooking.event_name || '—'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={eventBooking.status || 'tentative'}
                              color={getStatusColor(eventBooking.status)}
                              size="small"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: textBody }}>{formatDate(eventBooking.event_date)}</TableCell>
                          <TableCell sx={{ color: textBody }}>{eventBooking.time || '—'}</TableCell>
                          <TableCell sx={{ color: textBody }}>{eventBooking.hall_name || '—'}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: textPrimary }}>
                            {formatCurrency(eventBooking.amount || 0, customer?.currency)}
                          </TableCell>
                          <TableCell sx={{ color: textBody }} align="right">
                            {eventBooking.guests ?? '—'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={paymentChip.label}
                              color={paymentChip.color}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            {safeEventId ? (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEventBookingClick(safeEventId);
                                }}
                                sx={{ color: accent }}
                              >
                                <ChevronRightIcon />
                              </IconButton>
                            ) : null}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          ) : null}
        </Grid>
      </Grid>

      <CustomerContactInfoDrawer
        open={contactDrawerOpen}
        onClose={() => {
          if (!contactSaving) setContactDrawerOpen(false);
        }}
        customer={customer}
        initialMode={contactDrawerMode}
        onSave={saveContactFromDrawer}
        saving={contactSaving}
      />

      <CustomerContactInfoDrawer
        open={stayGuestDrawer.open}
        onClose={() => {
          if (!stayGuestSaving) stayGuestDrawer.close();
        }}
        customer={stayGuestDrawer.guest}
        initialMode="view"
        allowEdit
        titleOverride={stayGuestDrawer.titleOverride}
        subtitleOverride={stayGuestDrawer.subtitleOverride}
        bookingId={stayGuestDrawer.bookingId}
        onSave={saveStayGuestFromDrawer}
        saving={stayGuestSaving}
      />

      <CustomerCompanyDetailsDrawer
        open={companyDrawerOpen}
        onClose={() => {
          if (!companySaving) setCompanyDrawerOpen(false);
        }}
        customer={customer}
        initialMode={companyDrawerMode}
        onSave={saveCompanyFromDrawer}
        saving={companySaving}
      />

      <ForeignGuestDetailsDrawer
        open={foreignGuestDrawerOpen}
        onClose={() => {
          if (!foreignGuestSaving) setForeignGuestDrawerOpen(false);
        }}
        onSave={saveForeignGuestDetails}
        hotelCountry={hotelCountry}
        guestCountry={customer?.country || ''}
        initialDetails={foreignGuestDetails}
        initialMode={foreignGuestDrawerMode}
      />

      <AlertDialog
        open={companyRemoveOpen}
        onClose={() => !companyRemoving && setCompanyRemoveOpen(false)}
        onConfirm={removeCompanyDetails}
        confirming={companyRemoving}
        severity="error"
        title="Remove company details?"
        message="This clears company name, tax ID, phone, email, and address for this guest."
        confirmLabel="Remove"
        cancelLabel="Keep details"
      />

      <AlertDialog
        open={foreignGuestRemoveOpen}
        onClose={() => !foreignGuestRemoving && setForeignGuestRemoveOpen(false)}
        onConfirm={removeForeignGuestDetails}
        confirming={foreignGuestRemoving}
        severity="error"
        title="Remove foreign guest details?"
        message="This clears passport, visa, and arrival details for this guest. Identity document images are kept."
        confirmLabel="Remove"
        cancelLabel="Keep details"
      />
      <Snackbar
        open={snackbar.open}
        autoHideDuration={8000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CustomerDetailPage;
